import React, { useMemo } from 'react';
import { ChartFactory } from '../../../../../ChartUtility/charts';

const THEME = {
  primary: "#3f88a7",
  primaryDark: "#2f6e8a",
  primaryLight: "#6aaec7",
  primaryFaint: "#e8f4f8",
  accent: "#f0a500",
  success: "#2ecc8b",
  danger: "#e05c5c",
  neutral: "#64748b",
  bg: "#f0f2f5",
  surface: "#ffffff",
  border: "#e2e8f0",
  text: "#1a2535",
  textMuted: "#94a3b8",
  textSub: "#64748b",
};

const IntensityUnitMap = {
  CarbonIntensity:            'kg CO₂e',
  WaterIntensity:             'kL',
  WasteIntensity:             'Kg',
  HazardousWasteIntensity:    'Kg',
  NonHazardousWasteIntensity: 'Kg',
  BioMedicalWasteIntensity:   'Kg',
  FuelIntensity:              'GJ',
  RenewableEnergyIntensity:   'kWh',
  ElectricityIntensity:       'kWh',
};

const IntensityGraphCard = ({
  rawData,
  selectedIntensityType,
  selectedPeriodsValue,
  selectedLocations,
  targetKpi,
  accentColor,
  moduleIcon,
}) => {

  // ── Original computation logic — completely unchanged ─────────────────
  const processedData = useMemo(() => {
    if (!rawData.length || !selectedIntensityType || !selectedPeriodsValue.length) return [];

    const validLocIds = selectedLocations.map(id => id.toString());
    const validMonths = selectedPeriodsValue.map(p => p.fromDate.substring(0, 7));

    const numeratorUnit = IntensityUnitMap[selectedIntensityType.value] || '';
    let denominatorUnit = '';

    if      (targetKpi.includes('Cr'))        denominatorUnit = 'Cr';
    else if (targetKpi.includes('sq. ft'))    denominatorUnit = 'sq. ft';
    else if (targetKpi.includes('Served'))    denominatorUnit = 'Patient';
    else if (targetKpi.includes('Beds'))      denominatorUnit = 'Bed';

    const finalUnit = numeratorUnit && denominatorUnit
      ? `${numeratorUnit}/${denominatorUnit}`
      : numeratorUnit;

    const periodSums = selectedPeriodsValue.reduce((acc, p) => {
      acc[p.label] = { metricValue: 0, answer: 0 };
      return acc;
    }, {});

    rawData.forEach(item => {
      const month = item.fromDate.substring(0, 7);
      if (
        item.type === selectedIntensityType.value &&
        validLocIds.includes(item.sourceId?.toString()) &&
        validMonths.includes(month)
      ) {
        const periodLabel = selectedPeriodsValue
          .find(p => p.fromDate.startsWith(month))?.label;
        if (!periodLabel) return;

        const ansData     = item.intensityQuestionAnswers?.find(a => a.title === targetKpi);
        const answer      = parseFloat(ansData?.answer      || 0);
        const metricValue = parseFloat(ansData?.metricValue || 0);

        periodSums[periodLabel].metricValue += metricValue;
        periodSums[periodLabel].answer      += answer;
      }
    });

    return selectedPeriodsValue.map(p => {
      const data = periodSums[p.label];
      return {
        displayPeriods: p.label,
        kpi:            targetKpi,
        sum_value:
          data.answer > 0 && data.metricValue > 0
            ? data.metricValue / data.answer
            : 0,
        unit: finalUnit,
      };
    });
  }, [rawData, selectedIntensityType, selectedPeriodsValue, selectedLocations, targetKpi]);
  // ─────────────────────────────────────────────────────────────────────

  const group_by = ['displayPeriods', 'kpi'];

  const accent = accentColor || THEME.primary;
  const iconBg = accent + "18";

  return (
    <div style={{
      background:    "#ffffff",
      borderRadius:  16,
      border:        `1px solid ${THEME.border}`,
      boxShadow:     "0 1px 4px rgba(0,0,0,0.06)",
      overflow:      "hidden",
      fontFamily:    "'DM Sans', system-ui, sans-serif",
      display:       "flex",
      flexDirection: "column",
      width:         "100%",
      boxSizing:     "border-box",
    }}>

      {/* ── Top accent stripe ── */}
      <div style={{ height: 3, background: accent, flexShrink: 0 }} />

      {/* ── Body ── */}
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 0, flex: 1 }}>

        {/* ── Title row ── */}
        <div style={{
          display:        "flex",
          alignItems:     "flex-start",
          justifyContent: "space-between",
          gap:            8,
          marginBottom:   14,
        }}>
          <div style={{
            fontFamily:      "'DM Sans', system-ui, sans-serif",
            fontSize:        13,
            fontWeight:      600,
            color:           "#374151",
            letterSpacing:   "-0.01em",
            lineHeight:      1.4,
            display:         "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow:        "hidden",
            flex:            1,
            paddingRight:    moduleIcon ? 4 : 0,
          }}>
            {targetKpi}
          </div>
          {moduleIcon && (
            <div style={{
              flexShrink:     0,
              width:          38,
              height:         38,
              borderRadius:   10,
              background:     iconBg,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              fontSize:       18,
            }}>
              {moduleIcon}
            </div>
          )}
        </div>

        {/* ── Divider ── */}
        <div style={{
          height:       1,
          background:   `linear-gradient(90deg, ${accent}40, #f1f5f9 60%, transparent)`,
          marginBottom: 14,
          flexShrink:   0,
        }} />

        {/* ── Chart ── */}
        {processedData.length > 0 ? (
          <ChartFactory
            chartData={{ data: processedData, chartType: 'line' }}
            chartOptions={{ height: 350, stacked: false, transform: true }}
            group_by={group_by}
          />
        ) : (
          <div style={{
            textAlign:  "center",
            padding:    "40px 0",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            fontSize:   12,
            color:      "#94a3b8",
          }}>
            No data available for current selection.
          </div>
        )}

      </div>
    </div>
  );
};

export default IntensityGraphCard;